/**
 * Generates a table of contents from headings and replaces [ld:toc] markers.
 * Syntax: [ld:toc] (h2-h3), [ld:toc:N] (h2-hN), or [ld:toc:M-N] (hM-hN)
 * Default starts at h2 since h1 is typically the document title.
 * Use [ld:toc:1-N] to include h1 if needed.
 * Creates clickable navigation links with smooth scrolling.
 *
 * Related marker definition: src/shared/markers.ts -> 'toc'
 */

import { BaseEnricher } from './BaseEnricher.js';
import type { EnricherConfig, EnricherContext } from './types.js';

interface TocConfig extends EnricherConfig {
  /** Maximum heading depth to include (1-6). Default: 3 */
  maxDepth?: number;
  /** Minimum heading depth to include (1-6). Default: 2 (skips h1 document title) */
  minDepth?: number;
}

interface HeadingInfo {
  level: number;
  text: string;
  id: string;
}

export class TocEnricher extends BaseEnricher {
  /** Marker name in shared/markers.ts registry */
  static readonly markerName = 'toc';

  private maxDepth: number;
  private minDepth: number;

  constructor(config: TocConfig = {}) {
    super({ priority: 95, ...config }); // Run before most enrichers
    this.maxDepth = config.maxDepth ?? 3;
    this.minDepth = config.minDepth ?? 2; // Default to h2, skipping h1 document title
  }

  async process(container: HTMLElement, _context: EnricherContext): Promise<void> {
    // Find [ld:toc] or [ld:toc:N] markers - marked renders them as <p>[ld:toc]</p>
    const tocMarkers = this.findTocMarkers(container);
    if (tocMarkers.length === 0) return;

    // Collect all headings from the document (will filter per-marker)
    const allHeadings = this.collectHeadings(container, 6);
    if (allHeadings.length === 0) {
      // Remove markers if no headings found
      tocMarkers.forEach(({ element }) => element.remove());
      return;
    }

    // Replace each marker with the TOC (filtered to its depth range)
    tocMarkers.forEach(({ element, minDepth, maxDepth }) => {
      const headings = allHeadings.filter(h => h.level >= minDepth && h.level <= maxDepth);
      if (headings.length === 0) {
        element.remove();
        return;
      }

      const tocElement = this.buildTocElement(headings, minDepth);
      element.replaceWith(tocElement);
    });

    // Add smooth scroll behavior for TOC links
    this.attachScrollHandlers(container);
  }

  /**
   * Find all [ld:toc], [ld:toc:N], or [ld:toc:M-N] markers in the content.
   * Returns markers with their configured depth range.
   */
  private findTocMarkers(container: HTMLElement): Array<{ element: Element; minDepth: number; maxDepth: number }> {
    const markers: Array<{ element: Element; minDepth: number; maxDepth: number }> = [];
    // Matches [ld:toc], [ld:toc:3], or [ld:toc:2-4]
    const tocPattern = /^\[ld:toc(?::(\d)(?:-(\d))?)?\]$/;

    // Check paragraphs for [ld:toc] markers
    container.querySelectorAll('p').forEach(p => {
      const text = p.textContent?.trim() ?? '';
      const match = text.match(tocPattern);
      if (match) {
        let minDepth = this.minDepth;
        let maxDepth = this.maxDepth;

        if (match[1]) {
          if (match[2]) {
            // Range syntax: [ld:toc:2-4]
            minDepth = parseInt(match[1], 10);
            maxDepth = parseInt(match[2], 10);
          } else {
            // Single depth: [ld:toc:4] means h2-h4 (uses default minDepth)
            maxDepth = parseInt(match[1], 10);
          }
        }

        // Clamp to valid range and ensure min <= max
        minDepth = Math.min(Math.max(minDepth, 1), 6);
        maxDepth = Math.min(Math.max(maxDepth, 1), 6);
        if (minDepth > maxDepth) [minDepth, maxDepth] = [maxDepth, minDepth];

        markers.push({ element: p, minDepth, maxDepth });
      }
    });

    return markers;
  }

  /**
   * Collect all headings within the specified depth range.
   */
  private collectHeadings(container: HTMLElement, maxDepth: number): HeadingInfo[] {
    const headings: HeadingInfo[] = [];
    const selector = this.buildHeadingSelector(maxDepth);

    container.querySelectorAll(selector).forEach(heading => {
      const level = parseInt(heading.tagName[1], 10);
      const text = heading.textContent?.trim() ?? '';
      let id = heading.id;

      // If heading doesn't have an ID, generate one
      if (!id) {
        id = this.slugify(text);
        heading.id = id;
      }

      if (text) {
        headings.push({ level, text, id });
      }
    });

    return headings;
  }

  /**
   * Build CSS selector for heading elements based on depth.
   */
  private buildHeadingSelector(maxDepth: number): string {
    const selectors: string[] = [];
    for (let i = this.minDepth; i <= maxDepth; i++) {
      selectors.push(`h${i}`);
    }
    return selectors.join(', ');
  }

  /**
   * Build the TOC element using DOM APIs (XSS-safe).
   */
  private buildTocElement(headings: HeadingInfo[], baseLevel: number): HTMLElement {
    const nav = document.createElement('nav');
    nav.className = 'toc';

    // Title
    const title = document.createElement('div');
    title.className = 'toc-title';
    title.textContent = 'Contents';
    nav.appendChild(title);

    // List
    const ul = document.createElement('ul');
    ul.className = 'toc-list';

    for (const heading of headings) {
      const indent = heading.level - baseLevel;

      const li = document.createElement('li');
      li.className = `toc-item toc-level-${indent}`;

      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.className = 'toc-link';
      link.textContent = heading.text; // Safe: textContent auto-escapes

      li.appendChild(link);
      ul.appendChild(li);
    }

    nav.appendChild(ul);
    return nav;
  }

  /**
   * Attach smooth scroll handlers to TOC links.
   */
  private attachScrollHandlers(container: HTMLElement): void {
    container.querySelectorAll('.toc-link').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const href = link.getAttribute('href');
        if (!href) return;

        const targetId = href.slice(1); // Remove #
        const target = document.getElementById(targetId);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Update URL hash without jumping
          history.pushState(null, '', href);
        }
      });
    });
  }

  /**
   * Convert text to a URL-friendly slug.
   */
  private slugify(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }

}
